import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Check, Info, Plus, Trash2, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { ROLE } from '@/lib/constants';


interface UserMapping {
  mappingid?: number;
  email: string;
  role: string;
}

export default function TeamPage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserMapping[]>([]);
  const [userData, setUserData] = useState<any>(null);
  const [accessRequests, setAccessRequests] = useState<any[]>([]);
  const [resolvingRequest, setResolvingRequest] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [memberEmail, setMemberEmail] = useState('');

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchAccessRequests();
  }, [appId]);

  const fetchUserData = async () => {
    try {
      const response = await sentinelNetwork.get('/entitlements');
      setUserData(response.data);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
  };

  const fetchUsers = async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const response = await sentinelNetwork.get(`/config/project/${appId}/team`);
      setUsers(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessRequests = async () => {
    if (!appId) return;
    try {
      const response = await sentinelNetwork.get(`/config/project/${appId}/access-requests`);
      setAccessRequests(response.data.data || []);
    } catch (err: any) {
      // Non-admins will get 403 — that's expected
      if (err.response?.status !== 403) {
        console.error('Failed to fetch access requests:', err);
      }
      setAccessRequests([]);
    }
  };

  const handleResolveRequest = async (email: string, action: 'APPROVE' | 'REJECT') => {
    setResolvingRequest(email);
    try {
      await sentinelNetwork.patch(`/config/project/${appId}/access-request/${encodeURIComponent(email)}`, { action });
      setSnackbar({
        open: true,
        message: action === 'APPROVE' ? 'Access request approved — user added as member' : 'Access request rejected',
        severity: 'success',
      });
      await Promise.all([fetchUsers(), fetchAccessRequests()]);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to resolve access request',
        severity: 'error',
      });
    } finally {
      setResolvingRequest(null);
    }
  };

  const currentUserEmail = (userData?.user_email || '').toLowerCase();
  const currentUserRoles: string[] = userData?.roles || [];
  const isAdmin = [ROLE.SENTINEL_PROJECT_CREATION, ROLE.SENTINEL_POWER_USER].some((r) => currentUserRoles.includes(r));

  const ALLOWED_EMAIL_DOMAINS = ['@mercedes-benz.com', '@msc.corpdir.net'];

  const duplicateUser = users.some(
    (u) => u.email?.toLowerCase() === memberEmail.trim().toLowerCase()
  );

  const isEmailValid =
    memberEmail.trim() === '' ||
    ALLOWED_EMAIL_DOMAINS.some((domain) =>
      memberEmail.trim().toLowerCase().endsWith(domain)
    );

  const handleAdd = async () => {
    if (!memberEmail.trim()) return;

    setSaving(true);
    try {
      await sentinelNetwork.post(`/config/project/${appId}/team`, {
        email: memberEmail.trim().toLowerCase(),
      });

      setSnackbar({
        open: true,
        message: 'Team member added successfully',
        severity: 'success',
      });

      await fetchUsers();
      setMemberEmail('');
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to add team member',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (user: UserMapping) => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/team/${encodeURIComponent(user.email)}`);

      setSnackbar({
        open: true,
        message: 'Team member removed',
        severity: 'success',
      });

      await fetchUsers();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: 'Failed to remove team member',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <Users size={24} color={theme.palette.primary.main} />
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h5" fontWeight={700}>
                Team Members
              </Typography>
              <Tooltip title="Members must raise a SENTINEL_USERS role in order to access the project." arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'default', color: 'text.secondary' }}>
                  <Info size={16} />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage team members for this project
          </Typography>
        </Box>

        {/* Pending Access Requests */}
        {isAdmin && accessRequests.length > 0 && (
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'warning.main',
              bgcolor: alpha(theme.palette.warning.main, 0.04),
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Pending Access Requests ({accessRequests.length})
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Requested</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accessRequests.map((req: any) => (
                      <TableRow key={req.email} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {req.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{req.userName || '-'}</TableCell>
                        <TableCell>{req.requestType === 'management' ? 'Management' : 'Team Member'}</TableCell>
                        <TableCell>
                          {req.requested_at
                            ? new Date(req.requested_at).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                disabled={resolvingRequest === req.email}
                                onClick={() => handleResolveRequest(req.email, 'APPROVE')}
                              >
                                {resolvingRequest === req.email ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Check size={18} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={resolvingRequest === req.email}
                                onClick={() => handleResolveRequest(req.email, 'REJECT')}
                              >
                                <X size={18} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Add Member Form */}
        {isAdmin && (
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Email"
                    value={memberEmail}
                    onChange={(e) => setMemberEmail(e.target.value)}
                    fullWidth
                    size="small"
                    required
                    error={duplicateUser || (!isEmailValid && memberEmail.trim() !== '')}
                    helperText={
                      duplicateUser
                        ? 'User is already a team member'
                        : !isEmailValid && memberEmail.trim() !== ''
                          ? 'Email must be @mercedes-benz.com or @msc.corpdir.net'
                          : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Plus size={20} />
                      )
                    }
                    onClick={handleAdd}
                    disabled={
                      saving ||
                      duplicateUser ||
                      !memberEmail.trim() ||
                      !isEmailValid
                    }
                    sx={{ height: 40, fontWeight: 600 }}
                  >
                    {saving ? 'Adding...' : 'Add Member'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Members Table */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Current Members ({users.length})
            </Typography>
            {users.length > 0 ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user, index) => (
                      <TableRow key={`${user.email}-${index}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell align="right">
                          {currentUserEmail !== user.email?.toLowerCase() && isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemove(user)}
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card
                elevation={0}
                sx={{ border: '1px dashed', borderColor: 'divider', p: 3 }}
              >
                <Stack alignItems="center" spacing={1}>
                  <Users size={36} color={theme.palette.text.disabled} />
                  <Typography variant="body2" color="text.secondary">
                    No team members added yet
                  </Typography>
                </Stack>
              </Card>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
